import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import { Button } from '@apollo/orbit';
import { FormikHelpers } from 'formik';
import React from 'react';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { HeadersEditor } from 'src/components/headersEditor/HeadersEditor';
import { NewHeaderButton } from 'src/components/headersEditor/NewHeaderButton';

import { HeaderEntry } from '../../explorerPage/hooks/useExplorerState/useHeadersManagerContext/shared';
import { LocalIntrospectionTab } from '../../subgraphsLayout/subgraphsPage/addSubgraphModal/LocalIntrospectionTab';
import { ProvideSchemaTab } from '../../subgraphsLayout/subgraphsPage/addSubgraphModal/ProvideSchemaTab';

export interface AdvancedOptionsSectionProps<T> {
  headers: HeaderEntry[];
  setHeaders: React.Dispatch<React.SetStateAction<HeaderEntry[]>>;
  setSchemaUploaded: React.Dispatch<React.SetStateAction<boolean>>;
  advancedOptionsDrawerOpen: boolean;
  setAdvancedOptionsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  advancedOptionsTab: 'headers' | 'schemaFile' | 'localIntrospection';
  setAdvancedOptionsTab: React.Dispatch<
    React.SetStateAction<'headers' | 'schemaFile' | 'localIntrospection'>
  >;
  setIsUsingLocalIntrospection: React.Dispatch<React.SetStateAction<boolean>>;
  setFieldValue: FormikHelpers<T>['setFieldValue'];
  stableHeaders: Record<string, string>;
  sdl: string;
  localEndpointUrl: string;
}

export const AdvancedOptionsSection = <T,>({
  headers,
  setHeaders,
  setSchemaUploaded,
  advancedOptionsDrawerOpen,
  setAdvancedOptionsDrawerOpen,
  advancedOptionsTab,
  setAdvancedOptionsTab,
  setIsUsingLocalIntrospection,
  setFieldValue,
  stableHeaders,
  sdl,
  localEndpointUrl,
}: AdvancedOptionsSectionProps<T>) => {
  return (
    <>
      <Button
        type="button"
        onClick={() => setAdvancedOptionsDrawerOpen((prev) => !prev)}
        variant="buttonLink"
        rightIcon={
          advancedOptionsDrawerOpen ? <IconChevronUp /> : <IconChevronDown />
        }
      >
        Advanced options
      </Button>
      {advancedOptionsDrawerOpen && (
        <>
          <div className="mb-2 flex gap-6 border-b border-primary">
            {/* TODO (a11y): It would be so much easier to use Chakra's Tab 
                  component here instead of state plus it would make this more 
                  accessible */}
            <HeaderTab
              className="focus-visible:focus-ring mx-0 text-base"
              tabID="headers"
              tabName="Provide HTTP headers"
              isActive={() => advancedOptionsTab === 'headers'}
              onClick={() => setAdvancedOptionsTab('headers')}
            />
            <HeaderTab
              className="focus-visible:focus-ring mx-0 text-base"
              tabID="schemaFile"
              tabName="Provide schema"
              isActive={() => advancedOptionsTab === 'schemaFile'}
              onClick={() => setAdvancedOptionsTab('schemaFile')}
            />
            <HeaderTab
              className="focus-visible:focus-ring mx-0 text-base"
              tabID="localIntrospection"
              tabName="Local introspection"
              isActive={() => advancedOptionsTab === 'localIntrospection'}
              onClick={() => setAdvancedOptionsTab('localIntrospection')}
            />
          </div>
          {advancedOptionsTab === 'headers' && (
            <div>
              <div className="text-base text-secondary">
                Add as many as required.
              </div>
              <HeadersEditor
                className="mt-3"
                headerDefinitions={headers}
                setHeaderDefinitions={setHeaders}
                withCheckbox={false}
                // TODO - the HeadersEditor component is confusingly used in Studio.
                // It was made for the Explorer which shows both shared & tab headers.
                // It is abused here to show headers. This component
                // should be split up so we can pass props that make sense
                // for just introspection headers
                headersDisabled={false}
                sharedHeadersDisabled={false}
              />
              <NewHeaderButton
                className="mt-2"
                isSharedHeader={false}
                onClick={() =>
                  setHeaders(
                    headers.concat({
                      headerName: '',
                      value: '',
                      checked: true,
                      key: Math.random(),
                    }),
                  )
                }
              />
            </div>
          )}
          {advancedOptionsTab === 'schemaFile' && (
            <ProvideSchemaTab
              sdl={sdl}
              setSdl={(value) => setFieldValue('sdl', value, true)}
              setSchemaUploaded={(value) => {
                setSchemaUploaded(value);
                setIsUsingLocalIntrospection(false);
                setFieldValue('localEndpointUrl', '');
              }}
            />
          )}
          {advancedOptionsTab === 'localIntrospection' && (
            <LocalIntrospectionTab
              localEndpointUrl={localEndpointUrl}
              setLocalEndpointUrl={(value) =>
                setFieldValue('localEndpointUrl', value)
              }
              setSdl={(value) => setFieldValue('sdl', value, true)}
              setSchemaUploaded={(value) => {
                setSchemaUploaded(value);
                setIsUsingLocalIntrospection(true);
              }}
              stableHeaders={stableHeaders}
            />
          )}
        </>
      )}
    </>
  );
};
